/* SoraKsa — merged theme behaviour.
   Navigation, predictive search, bag drawer and the gift-set product builder come from the
   base theme; the campaign hero carousel, gallery thumbs and quantity stepper come from v2. */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  const config = JSON.parse($('#store-config')?.textContent || '{}');
  const root = config.root || '/';

  function el(tag, text, cls) {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (cls) node.className = cls;
    return node;
  }

  /* ---------- dialogs ---------- */

  let returnFocus = null;

  function openDialog(dialog) {
    returnFocus = document.activeElement;
    dialog.showModal();
  }

  $$('[data-close]').forEach((button) =>
    button.addEventListener('click', () => button.closest('dialog').close())
  );
  $$('dialog').forEach((dialog) =>
    dialog.addEventListener('close', () => returnFocus?.focus())
  );

  /* ---------- mega menu ---------- */

  const closeMenus = () =>
    $$('.nav-entry').forEach((entry) => {
      $('.nav-toggle', entry)?.setAttribute('aria-expanded', 'false');
      const panel = $('.mega', entry);
      if (panel) panel.hidden = true;
    });

  $$('.nav-entry').forEach((entry) => {
    const toggle = $('.nav-toggle', entry);
    const panel = $('.mega', entry);
    if (!toggle || !panel) return;
    let timer;

    const open = () => {
      clearTimeout(timer);
      closeMenus();
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    };

    toggle.addEventListener('click', () => (panel.hidden ? open() : closeMenus()));
    entry.addEventListener('mouseenter', () => {
      if (matchMedia('(min-width:861px) and (hover:hover)').matches) open();
    });
    entry.addEventListener('mouseleave', () => {
      if (innerWidth > 860) timer = setTimeout(closeMenus, 120);
    });
    entry.addEventListener('focusout', () =>
      setTimeout(() => {
        if (!entry.contains(document.activeElement)) closeMenus();
      }, 0)
    );
  });

  const setMenu = (open) => {
    const nav = $('#main-nav');
    if (!nav) return;
    if (open) {
      const bar = $('.sk-header-bar');
      if (bar) nav.style.top = `${Math.max(0, Math.round(bar.getBoundingClientRect().bottom))}px`;
      nav.setAttribute('data-open', '');
      document.body.style.overflow = 'hidden';
    } else {
      nav.removeAttribute('data-open');
      nav.style.top = '';
      document.body.style.overflow = '';
    }
    $('.mobile-menu-toggle')?.setAttribute('aria-expanded', String(open));
  };

  $('.mobile-menu-toggle')?.addEventListener('click', () => {
    setMenu(!$('#main-nav')?.hasAttribute('data-open'));
  });

  addEventListener('resize', () => {
    if (innerWidth > 860) setMenu(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeMenus();
    $('#predictive-results')?.setAttribute('hidden', '');
    setMenu(false);
  });

  /* ---------- predictive search ---------- */

  const query = $('#header-query');
  const results = $('#predictive-results');
  let searchTimer;
  let aborter;

  query?.addEventListener('input', () => {
    clearTimeout(searchTimer);
    aborter?.abort();
    const term = query.value.trim();
    results.replaceChildren();
    results.hidden = true;
    if (term.length < 2) return;

    searchTimer = setTimeout(async () => {
      const current = new AbortController();
      aborter = current;
      try {
        const url = new URL(config.predictive + '.json', location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product,collection');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][fields]', 'title,product_type,tag,variants.title');

        const response = await fetch(url, { signal: current.signal });
        if (!response.ok) return;
        const data = (await response.json()).resources.results;
        if (query.value.trim() !== term) return;

        for (const [type, label] of [['collections', 'Collections'], ['products', 'Products']]) {
          if (!data[type]?.length) continue;
          results.append(el('h3', label));
          for (const item of data[type]) {
            const link = el('a');
            link.href = item.url;
            if (type === 'products' && item.image) {
              const img = el('img');
              img.src = typeof item.image === 'string' ? item.image : item.image.url;
              img.alt = '';
              link.append(img);
            }
            link.append(el('span', item.title));
            results.append(link);
          }
        }
        if (!results.children.length) {
          results.append(el('p', 'No suggestions. Press Search to see all results.'));
        }
        results.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') results.hidden = true;
      }
    }, 220);
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.header-search') && results) results.hidden = true;
  });

  /* ---------- bag drawer ---------- */

  async function showBag() {
    document.dispatchEvent(new CustomEvent('sk:bag-open'));
  }

  /* ---------- campaign hero carousel ---------- */

  function initHero(hero) {
    if (hero.dataset.initialized) return;
    hero.dataset.initialized = 'true';

    const track = $('[data-hero-track]', hero);
    const slides = $$('[data-hero-slide]', hero);
    const dots = $$('[data-hero-dot]', hero);
    if (!track || slides.length < 2) return;

    let index = 0;
    let timer = null;
    const autoplay = hero.getAttribute('data-autoplay') === 'true';
    const interval = parseInt(hero.getAttribute('data-interval'), 10) || 4000;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

    function go(next) {
      index = (next + slides.length) % slides.length;
      track.style.transform = `translate3d(-${index * 100}%,0,0)`;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        if (i === index) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function schedule() {
      if (!autoplay || reduced || hero.getAttribute('data-paused') === 'true') return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        go(index + 1);
        schedule();
      }, interval);
    }

    hero.addEventListener('click', (event) => {
      const control = event.target.closest('[data-hero-prev],[data-hero-next],[data-hero-dot]');
      if (!control) return;
      if (control.hasAttribute('data-hero-prev')) go(index - 1);
      else if (control.hasAttribute('data-hero-next')) go(index + 1);
      else go(Number(control.getAttribute('data-hero-dot')));
      schedule();
    });

    hero.addEventListener('mouseenter', () => clearTimeout(timer));
    hero.addEventListener('mouseleave', schedule);

    go(0);
    schedule();
  }

  /* ---------- quantity steppers ---------- */

  function initQuantity(control) {
    if (control.dataset.initialized) return;
    control.dataset.initialized = 'true';
    const input = $('[data-qty-input]', control);
    if (!input) return;

    control.addEventListener('click', (event) => {
      const button = event.target.closest('[data-qty-up],[data-qty-down]');
      if (!button) return;
      const value = parseInt(input.value, 10) || 1;
      input.value = button.hasAttribute('data-qty-up') ? value + 1 : Math.max(1, value - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- product ---------- */

  function initializeProduct(section) {
    if (section.dataset.initialized) return;
    section.dataset.initialized = 'true';

    const data = JSON.parse($('[data-product-json]', section).textContent);
    const labels = data.labels || {};
    const form = $('.product-form', section);
    if (!form) return;

    const native = $('[name=id]', form);
    const options = $$('.option-slot', form);
    const buttons = $$('[data-add]', section);
    const status = $('[data-selection-status]', form);
    const error = $('[data-product-error]', form);

    const initial = data.variants.find((v) => String(v.id) === native.value) || data.variants[0];
    let choices =
      data.gift && !data.defaultVariant
        ? Array(options.length).fill(null)
        : [...(initial?.options || [])];
    let current = initial;
    let busy = false;

    if (options.length) {
      const nativeWrap = $('.native-variant', form);
      const enhanced = $('.enhanced-options', form);
      if (nativeWrap) nativeWrap.hidden = true;
      if (enhanced) enhanced.hidden = false;
    }

    function update() {
      current = options.length
        ? data.variants.find((v) => v.options.every((name, i) => name === choices[i]))
        : initial;

      const completed = options.length ? choices.filter((value) => value !== null).length : 0;

      $$('[data-option-value]', form).forEach((button) =>
        button.setAttribute(
          'aria-pressed',
          String(choices[Number(button.dataset.optionIndex)] === button.dataset.optionValue)
        )
      );

      options.forEach((slot, i) => {
        const label = $('[data-choice-label]', slot);
        if (label) label.textContent = choices[i] || labels.choosePrompt || 'Choose your scent';
      });

      const incomplete = options.length && completed < options.length;
      const available = !!current?.available;

      buttons.forEach((button) => {
        button.disabled = busy || data.placeholder || incomplete || !available;
        button.textContent = data.placeholder
          ? labels.comingSoon || 'Coming soon'
          : incomplete
            ? `Choose ${options.length - completed} more`
            : !current
              ? labels.unavailable || 'Unavailable combination'
              : !available
                ? labels.soldOut || 'Sold out'
                : data.gift
                  ? labels.addGift || 'Add set to bag'
                  : labels.add || 'Add to bag';
      });

      if (status) {
        status.textContent = incomplete
          ? `${completed} of ${options.length} selected`
          : !current
            ? 'This combination is unavailable. Try another selection.'
            : !available
              ? 'This selection is currently sold out.'
              : data.gift
                ? 'Your set is ready to add.'
                : '';
      }

      if (current) {
        native.value = String(current.id);
        if (!data.placeholder) {
          $$('[data-product-price],[data-mobile-price]', section).forEach(
            (node) => (node.textContent = current.priceText)
          );
        }
        const url = new URL(location.href);
        url.searchParams.set('variant', current.id);
        history.replaceState({}, '', url);
      }
    }

    $$('[data-option-value]', form).forEach((button) =>
      button.addEventListener('click', () => {
        const index = Number(button.dataset.optionIndex);
        choices[index] = button.dataset.optionValue;
        update();
        options[index].open = false;
        const next = choices.findIndex((value) => value === null);
        if (next >= 0) {
          options[next].open = true;
          $('summary', options[next]).focus({ preventScroll: true });
        } else {
          $('summary', options[index]).focus({ preventScroll: true });
        }
      })
    );

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy || data.placeholder || !current?.available || choices.some((v) => v === null)) return;
      if (!form.reportValidity()) return;

      busy = true;
      update();
      if (error) error.textContent = '';

      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) {
          const problem = await response.json();
          throw Error(problem.description || 'This selection could not be added. Please try again.');
        }
        await showBag().catch(() => location.assign(config.cart));
      } catch (problem) {
        if (error) error.textContent = problem.message || 'Please try again.';
      } finally {
        busy = false;
        update();
      }
    });

    /* gallery thumbs */
    const main = $('[data-gallery-main]', section);
    $$('[data-gallery-thumb]', section).forEach((thumb) =>
      thumb.addEventListener('click', () => {
        if (!main) return;
        main.removeAttribute('srcset');
        main.src = thumb.getAttribute('data-full');
        main.alt = thumb.getAttribute('data-alt') || main.alt;
        $$('[data-gallery-thumb]', section).forEach((other) =>
          other.classList.toggle('is-active', other === thumb)
        );
      })
    );

    update();
  }

  /* ---------- cart line quantities ---------- */

  function initCartLines(scope) {
    $$('[data-cart-line-form]', scope).forEach((form) =>
      form.addEventListener('change', () => form.submit())
    );
  }

  /* ---------- boot ---------- */

  function boot(scope = document) {
    $$('[data-campaign-hero]', scope).forEach(initHero);
    $$('.quantity-control', scope).forEach(initQuantity);
    $$('[data-product-section]', scope).forEach(initializeProduct);
    initCartLines(scope);
  }

  boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();

/* ---------- quick add to bag from a product card ---------- */

(() => {
  const root = window.Shopify?.routes?.root || '/';

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('button[data-quick-add]');
    if (!button || button.disabled) return;
    event.preventDefault();

    const id = Number(button.dataset.variantId);
    if (!id) return;

    const original = button.textContent;
    button.disabled = true;

    try {
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] })
      });
      if (!response.ok) throw Error('Could not add that to the bag.');

      const cart = await fetch(root + 'cart.js').then((r) => r.json());
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });

      document.dispatchEvent(new CustomEvent('sk:bag-open'));

      button.classList.add('is-done');
      button.textContent = '✓';
    } catch (error) {
      button.textContent = '!';
    }

    setTimeout(() => {
      button.classList.remove('is-done');
      button.textContent = original;
      button.disabled = false;
    }, 1600);
  });
})();

/* ---------- gift set builder ---------- */

(() => {
  const root = document.querySelector('[data-gift-builder]');
  if (!root) return;

  const shopRoot = window.Shopify?.routes?.root || '/';
  const cfg = JSON.parse(root.querySelector('[data-gift-json]').textContent);
  const cards = [...root.querySelectorAll('[data-choice]')];
  const slots = [...root.querySelectorAll('[data-slot]')];
  const submit = root.querySelector('[data-submit]');
  const submitLabel = root.querySelector('[data-submit-label]');
  const errorEl = root.querySelector('[data-error]');
  const wasEl = root.querySelector('[data-was]');
  const saveEl = root.querySelector('[data-save]');
  const quick = root.querySelector('[data-quick]');
  const quickBody = root.querySelector('[data-quick-body]');
  const chosen = [];

  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(cents / 100);

  const pickFrom = (card) => ({
    handle: card.dataset.handle,
    title: card.dataset.title,
    price: Number(card.dataset.price),
    image: card.dataset.image
  });

  const render = () => {
    slots.forEach((slot, i) => {
      const pick = chosen[i];
      const img = slot.querySelector('img');
      const clear = slot.querySelector('[data-clear]');
      const label = slot.querySelector('[data-slot-label]');
      slot.classList.toggle('is-filled', Boolean(pick));
      if (pick) {
        img.src = pick.image;
        img.alt = pick.title;
        img.hidden = false;
        clear.hidden = false;
        label.textContent = pick.title;
      } else {
        img.hidden = true;
        img.removeAttribute('src');
        clear.hidden = true;
        label.textContent = cfg.slotLabel + ' ' + (i + 1);
      }
    });

    const full = chosen.length >= cfg.slots;

    cards.forEach((card) => {
      if (card.classList.contains('is-sold-out')) return;
      const button = card.querySelector('[data-add]');
      const count = chosen.filter((c) => c.handle === card.dataset.handle).length;
      button.disabled = full;
      card.classList.toggle('is-chosen', count > 0);
      let text = cfg.labels.add;
      if (count === 1) text = cfg.labels.added;
      if (count > 1) text = cfg.labels.added + ' × ' + count;
      button.querySelector('[data-add-label]').textContent = text;
    });

    submit.disabled = !full;
    submitLabel.textContent = full
      ? cfg.labels.ready
      : cfg.labels.empty.replace('[n]', cfg.slots - chosen.length);

    const total = chosen.reduce((sum, c) => sum + c.price, 0);
    const was = full ? total : cfg.baseline;
    if (wasEl) {
      wasEl.textContent = money(was);
      wasEl.hidden = was <= cfg.price;
    }
    if (saveEl) {
      saveEl.textContent = 'Save ' + money(was - cfg.price);
      saveEl.hidden = was - cfg.price <= 0;
    }
  };

  root.addEventListener('click', (event) => {
    const add = event.target.closest('[data-add]');
    if (add) {
      if (chosen.length >= cfg.slots) return;
      const card =
        add.closest('[data-choice]') ||
        cards.find((c) => c.dataset.handle === add.dataset.handle);
      if (!card || card.classList.contains('is-sold-out')) return;
      chosen.push(pickFrom(card));
      if (quick?.open) quick.close();
      render();
      return;
    }

    const thumb = event.target.closest('[data-thumb]');
    if (thumb) {
      const stage = quickBody?.querySelector('[data-stage]');
      if (stage) stage.src = thumb.dataset.thumb;
      quickBody.querySelectorAll('[data-thumb]').forEach((t) => {
        t.classList.toggle('is-active', t === thumb);
      });
      return;
    }

    if (event.target.closest('[data-quick-close]')) {
      quick?.close();
      return;
    }

    const opener = event.target.closest('[data-open]');
    if (opener && quick && !event.target.closest('[data-add]')) {
      const template = opener.querySelector('[data-detail]');
      if (!template) return;
      quickBody.replaceChildren(template.content.cloneNode(true));
      const modalAdd = quickBody.querySelector('[data-add]');
      if (modalAdd && !modalAdd.disabled) modalAdd.disabled = chosen.length >= cfg.slots;
      quick.showModal();
      return;
    }

    const clear = event.target.closest('[data-clear]');
    if (clear) {
      chosen.splice(Number(clear.closest('[data-slot]').dataset.slot) - 1, 1);
      render();
      return;
    }

    const preset = event.target.closest('[data-preset]');
    if (preset) {
      const wanted = preset.dataset.preset.split(',').map((h) => h.trim()).filter(Boolean);
      chosen.length = 0;
      wanted.slice(0, cfg.slots).forEach((handle) => {
        const card = cards.find((c) => c.dataset.handle === handle);
        if (card && !card.classList.contains('is-sold-out')) chosen.push(pickFrom(card));
      });
      render();
      preset.closest('details')?.removeAttribute('open');
    }
  });

  quick?.addEventListener('click', (event) => {
    if (event.target === quick) quick.close();
  });

  submit.addEventListener('click', async () => {
    if (submit.disabled || chosen.length < cfg.slots) return;

    const name = cfg.slotLabel.charAt(0).toUpperCase() + cfg.slotLabel.slice(1);
    const properties = {};
    chosen.forEach((pick, i) => {
      properties[name + ' ' + (i + 1)] = pick.title;
    });

    submit.disabled = true;
    submitLabel.textContent = cfg.labels.adding;
    if (errorEl) errorEl.hidden = true;

    try {
      const response = await fetch(shopRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: cfg.id, quantity: 1, properties }] })
      });
      if (!response.ok) {
        const problem = await response.json();
        throw Error(problem.description || 'That set could not be added.');
      }
      const cart = await fetch(shopRoot + 'cart.js').then((r) => r.json());
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
      chosen.length = 0;
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
    }
    render();
  });

  render();
})();


/* --------------------------------------------------------------------------
   Header search — the icon swaps the header row for a full-width field
   -------------------------------------------------------------------------- */

(function () {
  var header = document.querySelector('[data-sk-header]');
  if (!header) return;
  var row = header.querySelector('.sk-search-row');
  var toggle = header.querySelector('[data-search-toggle]');
  if (!row || !toggle) return;
  var field = row.querySelector('input[type="search"]');

  function setOpen(open) {
    row.hidden = !open;
    if (open) header.setAttribute('data-search-open', '');
    else header.removeAttribute('data-search-open');
    toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      if (field) field.focus();
    } else {
      toggle.focus();
    }
  }

  toggle.addEventListener('click', function () {
    setOpen(!header.hasAttribute('data-search-open'));
  });

  row.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest('[data-search-close]')) setOpen(false);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && header.hasAttribute('data-search-open')) setOpen(false);
  });
})();


/* --------------------------------------------------------------------------
   Cart drawer — the bag slides in from the right, phone and desktop alike
   -------------------------------------------------------------------------- */

(function () {
  var dialog = document.getElementById('bag-dialog');
  if (!dialog) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var lines = dialog.querySelector('[data-bag-content]');
  var empty = dialog.querySelector('[data-bag-empty]');
  var foot = dialog.querySelector('[data-bag-foot]');
  var totalOut = dialog.querySelector('[data-bag-total]');
  var ship = dialog.querySelector('[data-bag-ship]');
  var shipText = dialog.querySelector('[data-bag-ship-text]');
  var shipBar = dialog.querySelector('[data-bag-ship-bar]');
  var threshold = Number(dialog.getAttribute('data-free-shipping')) || 0;
  var currency = 'USD';
  var busy = false;
  var returnFocus = null;

  function money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function node(tag, text, cls) {
    var n = document.createElement(tag);
    if (text !== undefined && text !== null) n.textContent = text;
    if (cls) n.className = cls;
    return n;
  }

  function render(cart) {
    currency = cart.currency || currency;
    lines.replaceChildren();

    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count]'), function (el) {
      el.textContent = cart.item_count;
    });

    cart.items.forEach(function (item, index) {
      var row = node('article', undefined, 'bag-line');
      row.setAttribute('data-line', String(index + 1));

      var media = node('div', undefined, 'bag-line-media');
      if (item.image) {
        var img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        media.append(img);
      }
      row.append(media);

      var copy = node('div', undefined, 'bag-line-copy');
      var link = node('a', item.product_title, 'bag-line-title');
      link.href = item.url;
      copy.append(link);
      copy.append(node('p', money(item.final_line_price), 'bag-line-price'));

      if (item.variant_title && item.variant_title !== 'Default Title') {
        copy.append(node('p', item.variant_title, 'bag-line-variant'));
      }
      Object.keys(item.properties || {}).forEach(function (key) {
        if (key.charAt(0) === '_' || !item.properties[key]) return;
        copy.append(node('p', key + ': ' + item.properties[key], 'bag-line-variant'));
      });

      var stepper = node('div', undefined, 'bag-qty');
      var minus = node('button', '−');
      minus.type = 'button';
      minus.setAttribute('data-bag-step', '-1');
      minus.setAttribute('aria-label', 'Decrease quantity');
      var plus = node('button', '+');
      plus.type = 'button';
      plus.setAttribute('data-bag-step', '1');
      plus.setAttribute('aria-label', 'Increase quantity');
      stepper.append(minus, node('span', String(item.quantity)), plus);

      var remove = node('button', 'Remove', 'bag-remove');
      remove.type = 'button';
      remove.setAttribute('data-bag-remove', '');

      var controls = node('div', undefined, 'bag-line-controls');
      controls.append(stepper, remove);
      copy.append(controls);

      row.append(copy);
      lines.append(row);
    });

    var filled = cart.item_count > 0;
    lines.hidden = !filled;
    empty.hidden = filled;
    foot.hidden = !filled;
    if (totalOut) totalOut.textContent = money(cart.total_price);

    if (ship) {
      if (!threshold || !filled) {
        ship.hidden = true;
      } else {
        ship.hidden = false;
        var left = threshold - cart.total_price;
        shipText.textContent = left > 0
          ? 'Add ' + money(left) + ' more for free shipping'
          : 'You have earned free shipping.';
        shipBar.style.width = Math.max(0, Math.min(100, (cart.total_price / threshold) * 100)) + '%';
      }
    }
  }

  function refresh() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw Error('cart');
        return response.json();
      })
      .then(render);
  }

  function open() {
    if (!dialog.open) {
      returnFocus = document.activeElement;
      dialog.showModal();
    }
    requestAnimationFrame(function () { dialog.setAttribute('data-open', ''); });
  }

  function close() {
    dialog.removeAttribute('data-open');
    setTimeout(function () {
      if (dialog.open) dialog.close();
      if (returnFocus && returnFocus.focus) returnFocus.focus();
    }, 360);
  }

  function show() {
    refresh().then(open).catch(function () { location.assign(root + 'cart'); });
  }

  function change(line, quantity) {
    if (busy) return;
    busy = true;
    dialog.setAttribute('data-busy', '');
    fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) { if (cart) render(cart); })
      .finally(function () {
        busy = false;
        dialog.removeAttribute('data-busy');
      });
  }

  /* the bag icon opens the drawer instead of loading the cart page */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    if (event.target.closest('.sk-bag')) {
      event.preventDefault();
      show();
    }
  });

  dialog.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    if (event.target.closest('[data-bag-close]') || !event.target.closest('.bag-panel')) close();
  });

  dialog.addEventListener('cancel', function (event) {
    event.preventDefault();
    close();
  });

  lines.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var row = event.target.closest('[data-line]');
    if (!row) return;
    var line = Number(row.getAttribute('data-line'));

    var step = event.target.closest('[data-bag-step]');
    if (step) {
      var current = Number(row.querySelector('.bag-qty span').textContent) || 1;
      change(line, Math.max(0, current + Number(step.getAttribute('data-bag-step'))));
      return;
    }
    if (event.target.closest('[data-bag-remove]')) change(line, 0);
  });

  document.addEventListener('sk:bag-open', show);
})();
